import { type FieldDef, type FieldUi, type FormSchemaProperty } from '../types';

const AUTOCOMPLETE_ITEM_SCHEMA = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    label: { type: 'string' }
  },
  required: ['id'],
  additionalProperties: false
};

const selectValueSchema = (field: FieldDef) => {
  const enumSchema = { enum: (field.options ?? []).map((option) => option.value) };
  // An "Other" answer is any free string; enum values also match, so anyOf.
  return field.allowOther ? { anyOf: [enumSchema, { type: 'string' }] } : enumSchema;
};

// Converts a single builder FieldDef into its JSON Schema property fragment.
// The inverse of propertyToFieldDef; shared by toJsonSchema and by section
// creation (which needs a question's definition from a field).
const fieldToProperty = (field: FieldDef): FormSchemaProperty => {
  const ui: FieldUi = { widget: field.widget };
  if (field.placeholder) ui.placeholder = field.placeholder;
  if (field.description) ui.description = field.description;
  let valueSchema: Record<string, unknown>;

  switch (field.widget) {
    case 'text':
    case 'textarea':
      valueSchema = { type: 'string' };
      if (field.pattern) {
        valueSchema.pattern = field.pattern;
        if (field.patternMessage) ui.patternMessage = field.patternMessage;
      }
      break;
    case 'number':
      valueSchema = { type: 'number' };
      break;
    case 'date':
      valueSchema = { type: 'string', format: 'date' };
      break;
    case 'checkbox':
      valueSchema = { type: 'boolean' };
      break;
    case 'select':
      ui.options = field.options ?? [];
      if (field.allowOther) ui.allowOther = true;
      if (field.multiple) ui.multiple = true;
      valueSchema = field.multiple
        ? { type: 'array', items: selectValueSchema(field) }
        : selectValueSchema(field);
      break;
    case 'radio':
      ui.options = field.options ?? [];
      if (field.allowOther) ui.allowOther = true;
      valueSchema = selectValueSchema(field);
      break;
    case 'autocomplete':
      ui.model = field.model ?? 'staff';
      if (field.multiple) ui.multiple = true;
      valueSchema = field.multiple
        ? { type: 'array', items: AUTOCOMPLETE_ITEM_SCHEMA }
        : AUTOCOMPLETE_ITEM_SCHEMA;
      break;
    case 'html_content':
      ui.body = field.body ?? '';
      valueSchema = { type: 'null' };
      break;
    case 'image':
      ui.url = field.url ?? '';
      valueSchema = { type: 'null' };
      break;
    case 'section_header':
      valueSchema = { type: 'null' };
      break;
  }

  if (field.questionId) ui.questionId = field.questionId;
  if (field.inLibrary) ui.inLibrary = true;
  if (field.sectionKey) ui.sectionKey = field.sectionKey;

  return { ...valueSchema, title: field.label, 'x-ui': ui };
};

export default fieldToProperty;
